import { useRef, useState } from "react";
// @ts-ignore:next-line
import { PlacePicker as PlacePickerClass } from "@googlemaps/extended-component-library/react";
// @ts-ignore:next-line
import { PlacePicker } from "@googlemaps/extended-component-library/react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  APIProvider,
  AdvancedMarker,
  GoogleMapsContext,
  Map,
  Pin,
} from "@vis.gl/react-google-maps";
import { Circle } from "mt-components";
import { generateGoogleMapsUrl } from "mt-shared/maps";
import { BiPencil } from "react-icons/bi";
import { MdInfo } from "react-icons/md";
import styles from "./PickupLocation.module.css";
import { GoogleMapsQuery, PickupAnswer, PickupLocationType } from "./types";

type PickupLocationProps = {
  hasDefaultVehiclePickup: boolean;
  hasAccomodationVehiclePickup: boolean;
  setPickupLocation: React.Dispatch<React.SetStateAction<PickupLocationType>>;
  defaultMeetingPointQuery: GoogleMapsQuery;
  defaultMeetingPlaceName: string;
  pickupLocation: PickupLocationType;
  pickupLocationZone: {
    center: { lat: number; lng: number };
    radius: number;
  } | null;
  pickUpAnswer: PickupAnswer;
  setPickUpAnswer: React.Dispatch<React.SetStateAction<PickupAnswer>>;
  onChange?: (hasChanged: boolean) => void;
  action?: "confirm" | "edit";
};

const PickupLocation = ({
  hasAccomodationVehiclePickup,
  hasDefaultVehiclePickup,
  pickupLocationZone,
  setPickupLocation,
  pickupLocation,
  defaultMeetingPointQuery,
  defaultMeetingPlaceName,
  pickUpAnswer,
  setPickUpAnswer,
  onChange,
  action,
}: PickupLocationProps) => {
  const DEFAULT_CENTER = pickupLocationZone?.center;
  const DEFAULT_ZOOM = 10;
  const pickerRef = useRef<PlacePickerClass>(null);
  const [openIDK, setOpenIDK] = useState(false);
  const handleOpenIDKModal = () => setOpenIDK(true);
  const handleCloseIDKModal = () => setOpenIDK(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [accomodation, setAccomodation] =
    useState<google.maps.places.Place | null>(null);
  const [canConfirm, setCanConfirm] = useState(false);
  const defaultMeetingLocation = () => {
    setPickUpAnswer(PickupAnswer.DEFAULT_MEETING_PLACE);
    const newLocation: PickupLocationType = {
      query: {
        placeId: defaultMeetingPointQuery.placeId ?? "",
        geoLocation: {
          lat: defaultMeetingPointQuery.geoLocation?.lat ?? 0,
          lng: defaultMeetingPointQuery.geoLocation?.lng ?? 0,
        },
      },
      address: defaultMeetingPlaceName,
      name: "Default Meeting Point",
    };
    setPickupLocation(newLocation);
  };

  const handleLocationSelect = () => {
    if (!pickerRef.current?.value) {
      setAccomodation(null);
      setCanConfirm(false);
    } else {
      setAccomodation(pickerRef.current?.value);
      setCanConfirm(true);
      onChange?.(true);
    }
  };

  return (
    <div className="App">
      {pickUpAnswer && pickupLocation ? (
        <div>
          <a
            className={styles.MapUrl}
            href={generateGoogleMapsUrl(pickupLocation.query)}
            target="_blank"
          >
            {pickupLocation.name}
            <br />
            {pickupLocation.address}
          </a>
          <span
            className={styles.EditBtn}
            onClick={() => {
              setAccomodation(null);
              setPickupLocation(null);
            }}
          >
            <BiPencil className={styles.EditBtnIcon} />
            Edit
          </span>
        </div>
      ) : (
        <FormControl component="fieldset">
          {pickupLocation ? (
            <div>
              <a
                className={styles.MapUrl}
                href={generateGoogleMapsUrl(pickupLocation.query)}
                target="_blank"
              >
                {pickupLocation.name}
                <br />
                {pickupLocation.address}
              </a>
              <span
                className={styles.EditBtn}
                onClick={() => {
                  setAccomodation(null);
                  setPickupLocation(null);
                }}
              >
                <BiPencil className={styles.EditIcon} />
                Edit
              </span>
            </div>
          ) : (
            <RadioGroup
              value={pickUpAnswer}
              onChange={(e) => {
                setPickUpAnswer(e.target.value as PickupAnswer);
                onChange?.(true);
                if (e.target.value === PickupAnswer.DEFAULT_MEETING_PLACE) {
                  defaultMeetingLocation();
                } else if (e.target.value === PickupAnswer.IDK) {
                  setPickupLocation(null);
                }
              }}
            >
              <FormControlLabel
                value={PickupAnswer.IKNOW}
                control={<Radio />}
                label="Add pickup location now"
              />
              {hasDefaultVehiclePickup && hasAccomodationVehiclePickup && (
                <FormControlLabel
                  value={PickupAnswer.DEFAULT_MEETING_PLACE}
                  control={<Radio />}
                  label={
                    <>
                      Pickup at default meeting point
                      <span
                        style={{
                          fontSize: 11,
                          display: "block",
                          paddingBottom: action === "edit" ? "10px" : undefined,
                        }}
                      >
                        <a
                          className={styles.MapUrl}
                          href={generateGoogleMapsUrl({
                            placeId: defaultMeetingPointQuery.placeId ?? "",
                            geoLocation: {
                              lat:
                                defaultMeetingPointQuery.geoLocation?.lat ?? 0,
                              lng:
                                defaultMeetingPointQuery.geoLocation?.lng ?? 0,
                            },
                          })}
                          target="_blank"
                        >
                          (google map link)
                        </a>
                      </span>
                    </>
                  }
                />
              )}
              {action === "confirm" && (
                <FormControlLabel
                  value={PickupAnswer.IDK}
                  control={<Radio />}
                  label={
                    <span className={styles.DontKnowLabel}>
                      Add pickup location later
                      <MdInfo
                        size={24}
                        onClick={handleOpenIDKModal}
                        style={{ cursor: "pointer" }}
                      />
                    </span>
                  }
                />
              )}
            </RadioGroup>
          )}
        </FormControl>
      )}
      {pickupLocationZone &&
        !pickupLocation &&
        pickUpAnswer === PickupAnswer.IKNOW && (
          <APIProvider
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
            language={"en"}
          >
            <PlacePicker
              style={{ width: "100%" }}
              className="LocationPicker"
              ref={pickerRef}
              forMap="gmap"
              country={["jp"]}
              type="lodging"
              locationBias={DEFAULT_CENTER}
              radius={pickupLocationZone.radius}
              strictBounds={true}
              placeholder="Enter your accomodation hotel or hostel"
              onPlaceChange={handleLocationSelect}
            />
            {pickerRef.current?.value && (
              <p style={{ marginTop: 10 }}>
                {pickerRef.current?.value?.displayName}
              </p>
            )}
            <GoogleMapsContext.Provider value={{ map: mapInstance }}>
              <Map
                id="gmap"
                mapId="8c732c82e4ec29d9"
                defaultCenter={DEFAULT_CENTER}
                center={accomodation?.location || DEFAULT_CENTER}
                defaultZoom={DEFAULT_ZOOM}
                disableDefaultUI
                fullscreenControl={false}
                zoomControl={true}
                gestureHandling="greedy"
                style={{ height: "400px", marginTop: 20 }}
                onTilesLoaded={(event) => setMapInstance(event.map)}
              >
                {mapInstance && (
                  <Circle
                    center={DEFAULT_CENTER}
                    radius={pickupLocationZone.radius}
                    mapInstance={mapInstance}
                  />
                )}
                {accomodation?.location && (
                  <AdvancedMarker position={accomodation?.location}>
                    <Pin
                      background={"#FBBC04"}
                      glyphColor={"#000"}
                      borderColor={"#000"}
                    />
                  </AdvancedMarker>
                )}
              </Map>
            </GoogleMapsContext.Provider>
            <Button
              variant="contained"
              disabled={!canConfirm}
              style={{
                marginTop: "20px",
                backgroundColor: canConfirm ? "#f44336" : undefined,
              }}
              onClick={() =>
                setPickupLocation({
                  query: {
                    placeId: accomodation?.id || "",
                    geoLocation: {
                      lat: accomodation?.location?.lat() || 0,
                      lng: accomodation?.location?.lng() || 0,
                    },
                  },
                  address: pickerRef.current.value.formattedAddress,
                  name: pickerRef.current.value.displayName,
                })
              }
            >
              confirm pickup location
            </Button>
          </APIProvider>
        )}
      <Dialog
        open={openIDK}
        onClose={handleCloseIDKModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: "5px", padding: "10px" },
        }}
      >
        <DialogContent>
          <p>
            You can set a pickup location for the guest from the bookings list.
          </p>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseIDKModal} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default PickupLocation;
